// Sign-up flow v2 — the flow's static vocabulary: business types, connectable
// tools, module ("portion") catalogue and pricing. Ported from the approved
// v6 design (data.js). The prototype's sample numbers (NUM, SAMPLE_PLACE,
// PLACES, FLAGGED) are deliberately NOT here — the shipped flow only shows
// values that came back from the real API or from the owner's own typing.

export type TypeKey = 'rest' | 'qsr' | 'bake' | 'bar' | 'cater' | 'cpg';
export type ToolKey = 'square' | 'toast' | 'clover' | 'lightspeed' | 'shopify' | 'faire' | 'qbo';

export interface TypeDef {
  t: string; ex: string; c: string;
  unit: string; units: string | null;
  /** Register enum value — 'catering' is not in the API enum; see registerType(). */
  code: string;
}

export const TYPES: Record<TypeKey, TypeDef> = {
  rest: { t: 'Restaurant', ex: 'Full service, fine dining, bistro', c: '--p1', unit: 'location', units: 'locations', code: 'full_service' },
  qsr: { t: 'Quick service', ex: 'Counter, fast casual, food truck, ghost kitchen', c: '--p2', unit: 'location', units: 'locations', code: 'quick_service' },
  bake: { t: 'Café or bakery', ex: 'Coffee, bakery, gelato, cake maker', c: '--p3', unit: 'location', units: 'locations', code: 'bakery' },
  bar: { t: 'Bar', ex: 'Cocktail bar, pub, wine bar', c: '--p5', unit: 'bar', units: 'bars', code: 'bar' },
  cater: { t: 'Catering', ex: 'Caterer, event venue, meal prep', c: '--p4', unit: 'kitchen', units: 'kitchens', code: 'catering' },
  cpg: { t: 'Food brand', ex: 'Sauces, drinks, snacks sold wholesale or online', c: '--p6', unit: 'company', units: null, code: 'cpg' },
};

export const TYPE_KEYS = Object.keys(TYPES) as TypeKey[];

/** POST /auth/register restaurantType. 'catering' isn't in the enum — those
 * accounts register as full_service and keep the real segment in
 * settings.businessType (set right after registration). */
export const registerType = (k: TypeKey): string =>
  TYPES[k].code === 'catering' ? 'full_service' : TYPES[k].code;

/** Map a restaurant record back to a flow type (for resuming a flow that
 * lost its local state). */
export function typeFromRestaurant(apiType?: string | null, businessType?: string | null): TypeKey {
  if (businessType === 'catering') return 'cater';
  if (businessType === 'cpg' || apiType === 'cpg') return 'cpg';
  if (businessType === 'bakery' || apiType === 'bakery') return 'bake';
  switch (apiType) {
    case 'quick_service': case 'fast_casual': case 'food_truck': case 'ghost_kitchen': return 'qsr';
    case 'bar': return 'bar';
    case 'cafe': return 'bake';
    default: return 'rest';
  }
}

export interface ToolDef {
  n: string;
  /** What the tile promises — only what the connector actually delivers. */
  b: string;
  how: string;
  menu?: boolean; sales?: boolean; place?: boolean;
  /** Toast: customer-issued API credentials instead of OAuth. */
  creds?: boolean;
}

export const TOOLS: Record<ToolKey, ToolDef> = {
  square: { n: 'Square', b: 'Locations, menu and sales', menu: true, sales: true, place: true, how: 'OAuth' },
  toast: { n: 'Toast', b: 'Sales by item, every day', creds: true, sales: true, place: true, how: 'API credentials' },
  clover: { n: 'Clover', b: 'Recent orders and best sellers', sales: true, place: true, how: 'OAuth' },
  lightspeed: { n: 'Lightspeed', b: 'Your account, then sales', how: 'OAuth' },
  shopify: { n: 'Shopify', b: 'Orders and products', menu: true, sales: true, how: 'OAuth' },
  faire: { n: 'Faire', b: 'Wholesale orders and retailers', sales: true, how: 'OAuth or token' },
  qbo: { n: 'QuickBooks', b: 'Customers, invoices and what you are owed', how: 'OAuth' },
};

export const TOOLS_BY: Record<TypeKey, ToolKey[]> = {
  rest: ['square', 'toast', 'clover', 'lightspeed'],
  qsr: ['square', 'toast', 'clover', 'lightspeed'],
  bar: ['square', 'toast', 'clover', 'lightspeed'],
  cater: ['square', 'toast', 'clover', 'qbo'],
  bake: ['square', 'shopify', 'clover', 'qbo'],
  cpg: ['shopify', 'faire', 'qbo', 'square'],
};

export const SHARES: Record<ToolKey, string[]> = {
  square: ['Locations', 'Items and prices', 'Sales, last 7 days'],
  toast: ['Restaurant', 'Orders and items by day'],
  clover: ['Merchant', 'Recent orders', 'Best sellers'],
  lightspeed: ['Account'],
  shopify: ['Products', 'Orders', 'Customers'],
  faire: ['Wholesale orders', 'Retailers'],
  qbo: ['Customers', 'Invoices', 'Payments'],
};

/** Placeholder text for the opening-hours field, per segment. Never a value —
 * only what the input suggests. */
export const HOURS: Record<TypeKey, string> = {
  rest: 'Mon–Sun 11:00–22:00', qsr: 'Mon–Sun 10:30–21:00', bake: 'Mon–Sun 6:30–15:00',
  bar: 'Tue–Sun 17:00–01:00', cater: 'By event', cpg: '',
};

export const TZS = ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu', 'America/Toronto', 'America/Vancouver', 'Europe/London', 'Europe/Paris'];

export const ROLES = ['Manager', 'Server', 'Cook', 'Bartender', 'Host', 'Barista', 'Baker', 'Prep', 'Dishwasher', 'Driver', 'Production'];

export const VENDOR_CHIPS: Record<TypeKey, string[]> = {
  rest: ['Sysco', 'US Foods', 'Restaurant Depot', 'Baldor'],
  qsr: ['Sysco', 'US Foods', 'Restaurant Depot', 'PFG'],
  bake: ['King Arthur', 'Restaurant Depot', 'Ronnybrook Dairy', 'Baldor'],
  bar: ['Southern Glazer’s', 'RNDC', 'Breakthru Beverage', 'Baldor'],
  cater: ['Sysco', 'Restaurant Depot', 'Baldor', 'Party Rental Ltd'],
  cpg: ['Bulk ingredients', 'Packaging', 'Co-packer', 'Freight'],
};

/** Location-count estimate behind each answer — used only for the price
 * preview ("after your trial"), never shown as a fact. */
export const LOCS: Record<string, number> = { '1': 1, '2–4': 3, '5+': 8 };

export type ModItem = [id: string, name: string, price: number, desc: string, color: string, flat?: boolean];
export interface ModDef {
  core: { n: string; p: number; d: string };
  items: ModItem[];
  def: string[];
  flat?: boolean;
}

const REST_MODS: ModDef = {
  core: { n: 'Base', p: 199, d: 'Every integration, one view, the assistant' },
  items: [
    ['ops', 'Kitchen operations', 300, 'Inventory, photo counts, ordering, margins, invoices', '--p2'],
    ['staff', 'Staff', 139, 'Scheduling, training, labor', '--p4'],
    ['pos', 'POS + kitchen display', 149, 'Orders on iPhone, payments at cost', '--p1'],
    ['guest', 'Guest', 99, 'Reservations and guest history', '--p3'],
    ['bar', 'Bar', 69, 'Bar inventory by voice', '--p5'],
    ['events', 'Events & catering', 69, 'Linked to labor and inventory', '--p6'],
  ],
  def: ['ops', 'staff'],
};

export const MODS: Record<TypeKey, ModDef> = {
  rest: REST_MODS,
  qsr: { ...REST_MODS, def: ['ops', 'staff', 'pos'] },
  bake: {
    core: { n: 'Essentials', p: 299, d: 'Counts, ordering and recipes for one counter' },
    items: [
      ['batch', 'Batch planning', 50, 'What to bake, and the waste', '--p2'],
      ['pos', 'POS + kitchen display', 149, 'Counter orders, payments at cost', '--p1'],
      ['staff', 'Staff', 139, 'Scheduling, training, labor', '--p4'],
      ['whole', 'Wholesale', 99, 'Standing orders to other shops, per company', '--p6', true],
    ],
    def: ['batch'],
  },
  bar: {
    core: { n: 'Mise Bar', p: 99, d: 'Walk the shelves, say what you see' },
    items: [
      ['pos', 'POS', 149, 'Tabs on iPhone, payments at cost', '--p1'],
      ['staff', 'Staff', 139, 'Scheduling, training, labor', '--p4'],
      ['events', 'Events', 69, 'Private hires, planned', '--p6'],
    ],
    def: ['staff'],
  },
  cater: {
    core: { n: 'Base', p: 199, d: 'Every integration, one view, the assistant' },
    items: [
      ['events', 'Events & catering', 69, 'Quotes, orders and prep, linked to labor and inventory', '--p6'],
      ['ops', 'Kitchen operations', 300, 'Inventory, photo counts, ordering, margins, invoices', '--p2'],
      ['staff', 'Staff', 139, 'Scheduling, training, labor', '--p4'],
      ['pos', 'POS', 149, 'Take deposits and payments at cost', '--p1'],
    ],
    def: ['events', 'ops'],
  },
  cpg: {
    core: { n: 'Brand', p: 499, d: 'The whole company. Never per SKU or location' },
    items: [
      ['po', 'Purchase orders', 0, 'Email, Shopify and Faire orders in', '--p6'],
      ['prod', 'Production', 0, 'Planned against real demand', '--p2'],
      ['inv', 'Inventory', 0, 'Ingredients and finished goods', '--p3'],
      ['log', 'Logistics', 0, 'Short-pays and deductions reconciled', '--p4'],
    ],
    def: ['po', 'prod', 'inv', 'log'],
    flat: true,
  },
};

export const money = (n: number, cents?: boolean): string =>
  '$' + (cents
    ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : Math.round(n).toLocaleString('en-US'));

export function bizFromEmail(email: string): { name: string; domain: string } | null {
  const d = (email.split('@')[1] || '').toLowerCase();
  if (!d || /^(gmail|yahoo|outlook|hotmail|icloud|aol|proton|me|live)\./.test(d)) return null;
  let s = d.split('.')[0];
  const W = ['bakehouse', 'kitchen', 'bakery', 'bagels', 'coffee', 'cafe', 'foods', 'sauce', 'grill', 'tavern', 'pizza', 'tacos', 'brewing', 'market', 'house', 'bar', 'co'];
  for (const w of W) { const i = s.lastIndexOf(w); if (i > 0 && i + w.length === s.length) { s = s.slice(0, i) + ' ' + w; break; } }
  return { name: s.split(/[\s-]+/).map((x) => (x === 'co' ? 'Co.' : x[0].toUpperCase() + x.slice(1))).join(' '), domain: d };
}

export interface PriceResult { total: number; perLoc?: number; disc: number; per: boolean }

export function price(type: TypeKey, on: string[], locs: number): PriceResult {
  const m = MODS[type];
  if (!m) return { total: 0, disc: 0, per: false };
  if (m.flat) return { total: m.core.p, per: false, disc: 0 };
  let per = m.core.p, flat = 0;
  m.items.forEach(([id, , p, , , isFlat]) => { if (on.includes(id)) { if (isFlat) flat += p; else per += p; } });
  const disc = locs >= 10 ? 0.2 : locs >= 5 ? 0.1 : 0;
  return { total: per * locs * (1 - disc) + flat, perLoc: per, disc, per: true };
}

/** Trial end — always computed from today (+14 days), never hardcoded. */
export function trialEndDate(): string {
  return new Date(Date.now() + 14 * 86400000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/** The manager app on the App Store (same id the website links to). */
export const APP_STORE_URL = 'https://apps.apple.com/app/id6784768546';
